import { Inject, Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { Meta, Title } from "@angular/platform-browser";
import { EnvironmentConfig } from "../models/environment-config.model";
import { websiteRobots } from "./public-seo";

declare let environment: EnvironmentConfig;

@Injectable({
  providedIn: "root",
})
export class SeoService {
  clientNames = {
    "rubbl.website": {
      title: "Construction, Earth Moving & Heavy Equipment Rentals | Rubbl",
      description:
        "Rent heavy construction machinery and specialized equipment near you. Competitive prices, insurance, delivery options, and special rates available. Get started today!",
      keywords: "rubbl,heavy,equipment,construction,rental,rent,dirt,moving,dozer,excavator,caterpillar,near,me",
      image: "https://company.rubbl.com/pubweb/marketplace/images/logos/og-rubbl-dark.jpg",
    },
    "rubbl.admin": {
      title: "Admin | Rubbl",
      description: "",
      keywords: "",
      image: "https://company.rubbl.com/pubweb/marketplace/images/logos/og-rubbl-dark.jpg",
    },
  };

  private renderer: Renderer2;

  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  setSeo(routeData: SeoRouteData) {
    const clientMetadata = this.clientNames[environment.clientId];

    if (routeData.doNotUpdateSeo === true) {
      return;
    }

    if (routeData.robots) {
      this.setRobots(routeData.robots);
    }

    // if route data has a title set it, otherwise set it to a generic title
    if (routeData.title) {
      this.title.setTitle(`${routeData.title}`);
      this.meta.updateTag({ property: "og:title", content: routeData.title }); // og title
    } else if (clientMetadata.title) {
      this.title.setTitle(clientMetadata.title);
      this.meta.updateTag({ property: "og:title", content: clientMetadata.title }); // og title
    }

    // set keywords
    if (routeData.keywords) {
      this.meta.updateTag({ name: "keywords", content: routeData.keywords });
    } else if (clientMetadata.keywords) {
      this.meta.updateTag({ name: "keywords", content: clientMetadata.keywords });
    }

    // if route has description set it, otherwise remove meta description
    if (routeData.description) {
      this.meta.updateTag({ name: "description", content: routeData.description });
      this.meta.updateTag({ property: "og:description", content: routeData.description });
    } else if (clientMetadata.description) {
      this.meta.updateTag({ name: "description", content: clientMetadata.description });
      this.meta.updateTag({ property: "og:description", content: clientMetadata.description });
    }

    // set og url
    this.meta.updateTag({ property: "og:url", content: window.location.href });

    // set og img
    if (routeData.image) {
      this.meta.updateTag({ property: "og:image", content: routeData.image });
    } else if (clientMetadata.image) {
      this.meta.updateTag({ property: "og:image", content: clientMetadata.image });
    }

    window["prerenderReady"] = true;
  }

  setProductSeo(routeData: SeoRouteData, options: ProductSeoOptions) {
    this.setSeo(routeData);
    if (options.statusCode) {
      this.meta.updateTag({ name: "prerender-status-code", content: String(options.statusCode) });
    } else {
      this.meta.removeTag("name='prerender-status-code'");
    }

    const ogType = options.ogType ?? "product";
    this.meta.updateTag({ property: "og:type", content: ogType });

    if (options.canonicalUrl) {
      this.setCanonicalLink(options.canonicalUrl);
      this.meta.updateTag({ property: "og:url", content: options.canonicalUrl });
    }

    if (options.robots) {
      // Same host rules as setSeo, so a page that asks for noindex stays noindex
      // and dev/local renders are not marked index,follow.
      this.setRobots(options.robots);
    }

    if (options.ogImageWidth) {
      this.meta.updateTag({ property: "og:image:width", content: String(options.ogImageWidth) });
    } else {
      this.meta.removeTag("property='og:image:width'");
    }

    if (options.ogImageHeight) {
      this.meta.updateTag({ property: "og:image:height", content: String(options.ogImageHeight) });
    } else {
      this.meta.removeTag("property='og:image:height'");
    }

    if (options.twitter) {
      const twitter = options.twitter;
      this.meta.updateTag({ name: "twitter:card", content: twitter.card ?? "summary_large_image" });
      if (twitter.title) {
        this.meta.updateTag({ name: "twitter:title", content: twitter.title });
      }
      if (twitter.description) {
        this.meta.updateTag({ name: "twitter:description", content: twitter.description });
      }
      if (twitter.image) {
        this.meta.updateTag({ name: "twitter:image", content: twitter.image });
      }
      if (twitter.imageAlt) {
        this.meta.updateTag({ name: "twitter:image:alt", content: twitter.imageAlt });
      }
    }

    if (options.structuredData) {
      this.upsertJsonLd(options.structuredData, options.jsonLdKey);
    } else if (options.jsonLdKey) {
      this.removeJsonLd(options.jsonLdKey);
    } else {
      this.removeJsonLd();
    }
  }

  setRobots(requested: string) {
    const hostname = this.document?.location?.hostname || window.location.hostname;
    this.meta.updateTag({ name: "robots", content: websiteRobots(requested, environment.clientId, environment.production, hostname) });
  }

  upsertJsonLd(json: string, key: string = "seo") {
    if (!json || !this.document) {
      this.removeJsonLd(key);
      return;
    }

    try {
      JSON.parse(json);
    } catch {
      return;
    }

    const selector = `script[type='application/ld+json'][data-seo-${key}]`;
    let script = this.document.head?.querySelector(selector) as HTMLScriptElement;

    if (!script) {
      script = this.renderer.createElement("script");
      this.renderer.setAttribute(script, "type", "application/ld+json");
      this.renderer.setAttribute(script, `data-seo-${key}`, "true");
      this.renderer.appendChild(this.document.head, script);
    }

    script.textContent = json;
  }

  removeJsonLd(key: string = "seo") {
    if (!this.document) {
      return;
    }

    const selector = `script[type='application/ld+json'][data-seo-${key}]`;
    const existing = this.document.head?.querySelector(selector);
    existing?.parentElement?.removeChild(existing);
  }

  private setCanonicalLink(href: string) {
    if (!this.document || !href) {
      return;
    }

    const existingMetaCanonical = this.document.querySelector("meta[rel='canonical']");
    existingMetaCanonical?.parentElement?.removeChild(existingMetaCanonical);

    let canonicalLink = this.document.querySelector("link[rel='canonical']") as HTMLLinkElement;

    if (!canonicalLink) {
      canonicalLink = this.renderer.createElement("link");
      this.renderer.setAttribute(canonicalLink, "rel", "canonical");
      this.renderer.appendChild(this.document.head, canonicalLink);
    }

    this.renderer.setAttribute(canonicalLink, "href", href);
  }
}

export class SeoRouteData {
  robots?: string;
  doNotUpdateSeo: boolean;
  title: string;
  keywords: string;
  description: string;
  image: string;
}

export interface ProductSeoOptions {
  canonicalUrl: string;
  statusCode?: 200 | 404 | 410 | 503;
  structuredData?: string;
  robots?: string;
  ogType?: string;
  ogImageWidth?: string | number;
  ogImageHeight?: string | number;
  twitter?: {
    card?: string;
    title?: string;
    description?: string;
    image?: string;
    imageAlt?: string;
  };
  jsonLdKey?: string;
}
